import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useDisclosure } from '@chakra-ui/react';
import shared from './GarageContent.module.scss';
import classes from './AuctionInspections.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import MediaGallery from './MediaGallery';
import UpgradeModal from '../UI/UpgradeModal';
import AuthContext from '../../store/auth-context';
import { getUserInspections } from '../../services';
import { setCarDetail } from '../../store/reducers/carReducer';
import { formatDay } from '../../services/dealerGarage';
import placeholderImage from '../../media/images/emptyFavorites.png';

// Where the car page's Back link returns to.
const BACK_PATH = '/garage?source=auction&tab=inspections';

// The API's statuses as the chip on the card shows them.
const STATUS = {
    PENDING: { label: 'Requested', tone: shared.toneMuted },
    PROCESSING: { label: 'In progress', tone: shared.toneInfo },
    COMPLETED: { label: 'Report ready', tone: shared.toneBrand },
    CANCELLED: { label: 'Cancelled', tone: shared.toneDanger },
};

const FILTERS = [
    { id: 'all', label: 'All', match: () => true },
    { id: 'open', label: 'In progress', match: (status) => status === 'PENDING' || status === 'PROCESSING' },
    { id: 'done', label: 'Completed', match: (status) => status === 'COMPLETED' },
];

// "1998 Toyota Chaser Tourer V": the auction feed keeps the year in `model`.
const carTitle = (car) => {
    if (!car) return 'Auction car';
    const detail = car.makerDetail || {};
    // NFKC: the feed writes chassis codes full-width ("ＪＺＳ１７１"), which
    // render letter-spaced.
    return [car.model, detail.maker, detail.model, detail.type].filter(Boolean).join(' ').normalize('NFKC').trim() || 'Auction car';
};

const AuctionInspectionCard = ({ inspection }) => {
    const dispatch = useDispatch();
    const car = inspection.carData;
    const bid = car.bidDetail || {};
    const title = carTitle(car);
    const status = String(inspection.status || '').toUpperCase();
    const chip = STATUS[status] || { label: inspection.status || 'Unknown', tone: shared.toneMuted };
    const images = inspection.images || [];
    const videos = inspection.videos || [];
    const image = (car.vehicleImages || [])[0] || placeholderImage;

    return (
        <article className={shared.card}>
            <div className={shared.cardHead}>
                <div>
                    <span className={shared.typeTag}>Auction inspection</span>
                    {inspection.createdAt && (
                        <span className={shared.date}>Requested {formatDay(inspection.createdAt)}</span>
                    )}
                </div>
                <span className={`${shared.status} ${chip.tone}`}>{chip.label}</span>
            </div>

            {/* The detail page reads the car from the store when one is set;
                clear it so the page loads this car fresh. */}
            <Link
                to={`/detail/${car._id}`}
                state={{ from: BACK_PATH }}
                onClick={() => dispatch(setCarDetail(null))}
                className={shared.vehicle}
            >
                <img
                    src={image}
                    alt={title}
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }}
                />
                <div className={shared.vehicleText}>
                    <strong>{title}</strong>
                    <span>{[bid.bidSite, bid.bidNo && `Lot ${bid.bidNo}`].filter(Boolean).join(' · ')}</span>
                </div>
            </Link>

            <dl className={shared.meta}>
                {bid.bidDate && <div><dt>Auction date</dt><dd>{formatDay(bid.bidDate)}</dd></div>}
                {car.inspectionScore && <div><dt>Inspection score</dt><dd>{car.inspectionScore}</dd></div>}
            </dl>

            {status === 'COMPLETED' && (
                <section>
                    <h3 className={shared.subhead}>
                        <i className="fas fa-clipboard-check" aria-hidden="true"></i>
                        Inspection report
                    </h3>
                    {inspection.notes && <p className={shared.message}>{inspection.notes}</p>}
                    {(images.length > 0 || videos.length > 0) && (
                        <MediaGallery images={images} videos={videos} title={title} />
                    )}
                </section>
            )}
            {(status === 'PENDING' || status === 'PROCESSING') && (
                <p className={shared.message}>The inspector's report, photos and video show up here once the inspection is done.</p>
            )}
            {status === 'CANCELLED' && <p className={shared.message}>This inspection was cancelled.</p>}
        </article>
    );
};

/**
 * My Garage > Auction > Inspections: the member's auction inspection
 * requests as cards, in the same style as the dealer-stock inspections.
 */
const AuctionInspections = () => {
    const { isFreeTier } = useContext(AuthContext);
    const upgrade = useDisclosure();
    const [inspections, setInspections] = useState(null);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        let active = true;
        getUserInspections()
            .then((res) => {
                if (!active) return;
                // The API answers 404 when the member has none.
                if (Array.isArray(res?.data)) setInspections(res.data);
                else if (res?.status === 404) setInspections([]);
                else setFailed(true);
            })
            .catch(() => active && setFailed(true));
        return () => { active = false; };
    }, []);

    // Newest request first. A car that has left both car collections comes
    // back without carData and has nothing to show.
    const usable = useMemo(
        () => (inspections || [])
            .filter((i) => i.carData)
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        [inspections]
    );

    const visible = useMemo(() => {
        const match = FILTERS.find((f) => f.id === filter).match;
        const term = search.trim().toLowerCase();
        return usable.filter((i) => {
            if (!match(String(i.status || '').toUpperCase())) return false;
            if (!term) return true;
            const bid = i.carData.bidDetail || {};
            return [carTitle(i.carData), bid.bidNo, bid.bidSite]
                .some((v) => String(v || '').toLowerCase().includes(term));
        });
    }, [usable, filter, search]);

    const upgradeNote = isFreeTier && (
        <div className={classes.upgrade}>
            <i className="fas fa-lock" aria-hidden="true"></i>
            <p>Inspection requests are for paid members. Upgrade to have us inspect auction cars for you.</p>
            <button type="button" className={shared.secondaryLink} onClick={upgrade.onOpen}>Upgrade account</button>
            <UpgradeModal isOpen={upgrade.isOpen} onClose={upgrade.onClose} />
        </div>
    );

    if (failed) {
        return <div className={shared.error}>Could not load your auction inspections right now. Please try again in a moment.</div>;
    }
    if (inspections === null) {
        return <div className={shared.state}><Loading text="Inspections" /></div>;
    }
    if (usable.length === 0) {
        return (
            <>
                {upgradeNote}
                <div className={shared.state}>
                    <EmptyContentCard
                        header="No auction inspections yet"
                        message="Ask for an inspection from any auction car's page. The inspector's report, photos and video show up here."
                    />
                    <Link to="/auction" className={shared.primaryLink}>Browse Auctions</Link>
                </div>
            </>
        );
    }

    return (
        <>
            {upgradeNote}
            <div className={classes.toolbar}>
                <div className={classes.filters} role="group" aria-label="Show">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            aria-pressed={filter === f.id}
                            className={`${classes.filter} ${filter === f.id ? classes.filterActive : ''}`}
                            onClick={() => setFilter(f.id)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <label className={classes.search}>
                    <i className="fas fa-search" aria-hidden="true"></i>
                    <span className={shared.srOnly}>Search inspections</span>
                    <input
                        type="search"
                        placeholder="Car, lot or auction"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>
            </div>
            {visible.length === 0 ? (
                <p className={classes.none}>No inspections match.</p>
            ) : (
                <div className={shared.list}>
                    {visible.map((inspection) => (
                        <AuctionInspectionCard key={inspection._id} inspection={inspection} />
                    ))}
                </div>
            )}
        </>
    );
};

export default AuctionInspections;
